import React, { useContext, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { sample, trim } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import * as z from "zod";

import {
  Button,
  PrimaryAction,
  ResponsivePrimaryAction,
  ResponsiveTitleBar,
  TitleBar,
} from "../Atoms";
import { useBillSplit } from "../BillSplit/queries";
import {
  useCreateBillSplitCustomerSession,
  useCreateBillSplit,
} from "../BillSplit/queries";
import { useSession } from "../Session/SessionContext";
import { BustleBeeKeys, BustleBeeList, BustleBees } from "./bustleBees";
import { useCustomer, useSaveCustomer } from "./queries";
import { Customer } from "./schema";

const CustomerFormSchema = z.object({
  nickname: z.string().optional(),
  icon: z.string().optional(),
});
export type CustomerFormValues = z.infer<typeof CustomerFormSchema>;

export const QCustomerForm = ({ customer }: { customer?: Customer | null }) => {
  const { customerTab } = useSession();
  if (!customerTab) {
    throw "no customer tab";
  }
  const { data: billSplitSession } = useBillSplit(customerTab.uuid);
  const { t } = useTranslation();

  const createCustomerSession = useCreateBillSplitCustomerSession(customerTab);
  const createSession = useCreateBillSplit(customerTab);

  const navigate = useNavigate();
  const defaultNameAdjectives = t("customer.defaultNameAdjectives", {
    returnObjects: true,
  });
  const bustleBeeNames = t("customer.bustleBeeNames", {
    returnObjects: true,
  }) as any;

  const [defaultNameAdjective, _] = useState(sample(defaultNameAdjectives));
  const [defaultIcon, __] = useState(sample(BustleBeeList));
  const defaultIconValue = defaultIcon?.value;
  const [defaultName, ___] = useState(
    defaultIconValue
      ? sample(bustleBeeNames[defaultIconValue])
      : t("customer.defaultName")
  );
  const { register, handleSubmit, ...formProps } = useForm({
    resolver: zodResolver(CustomerFormSchema),
    defaultValues: {
      nickname: customer?.nickname,
      icon: customer?.icon || defaultIcon?.value,
    },
  });
  const saveCustomer = useSaveCustomer();
  const placeholderNickname = t("customer.defaultNickname", {
    adjective: defaultNameAdjective,
    name: defaultName,
  });
  const onSubmit = async (data: CustomerFormValues) => {
    const nickname = trim(data.nickname) || placeholderNickname;
    const customer = await saveCustomer.mutateAsync({ ...data, nickname });
    if (billSplitSession) {
      await createCustomerSession.mutateAsync();
    } else {
      await createSession.mutateAsync();
    }
    navigate("..");
  };
  return (
    <div className="max-w-screen-sm mx-auto">
      <ResponsiveTitleBar>{t("customer.addYourName")}</ResponsiveTitleBar>
      <form
        className="max-w-screen-sm p-16 space-y-32"
        onSubmit={handleSubmit(onSubmit)}
      >
        <label className="block space-y-16 font-bold text-xl">
          <div>{t("customer.nickname")}</div>
          <input
            ref={register}
            className="w-full p-12 shadow-venue-brand-style bg-venue-tertiary block rounded-venue-brand disabled:cursor-not-allowed"
            placeholder={placeholderNickname}
            name="nickname"
          />
        </label>
        <div className="block font-bold text-xl space-y-16">
          <div>{t("customer.pickYourIcon")}</div>
          <div className="mx-auto grid grid-cols-3 justify-between justify-items-center gap-32">
            {BustleBeeList.map((bee) => (
              <div key={bee.value} className="w-min group text-center ">
                <input
                  ref={register}
                  className="hidden peer"
                  name="icon"
                  value={bee.value}
                  id={`icon[${bee.value}]`}
                  type="radio"
                />
                <label
                  htmlFor={`icon[${bee.value}]`}
                  className={classNames(
                    "peer peer-checked:border-venue-primary border-venue-tertiary ",
                    " h-96 w-96 p-12 flex items-center justify-center rounded-full text-4xl cursor-pointer",
                    "border-4 bg-venue-tertiary "
                  )}
                >
                  <img src={bee.img_96} alt={bee.alt} className="" />
                </label>
              </div>
            ))}
          </div>
        </div>
        <ResponsivePrimaryAction>
          <Button primary>{t("customer.startSelecting")} </Button>
        </ResponsivePrimaryAction>
      </form>
    </div>
  );
};
export const CustomerForm = ({}: {}) => {
  const { data: customer, status } = useCustomer();
  const { t } = useTranslation();
  if (status == "pending") {
    return <div>Loading</div>;
  }
  return <QCustomerForm customer={customer} />;
};
